<script setup>
const route = useRoute();
const props = defineProps({
  items: Array,
  alternateActivePath: [String, null],
  theme: [String, null],
});

const listActive = ref(false);

const activeItem = computed(() => {
  if (props.alternateActivePath)
    return props.items?.find((item) => item.url === props.alternateActivePath);
  return props.items?.find((item) => item.url === route.fullPath);
});

const openOverlay = () => {
  useEvent("overlay:open", {
    data: {},
    type: "newsletter",
  });
};

const emit = defineEmits(["subnav-mobile-click"]);

const closeMenu = (url) => {
  listActive.value = false;
  emit("subnav-mobile-click", true);
};
</script>

<template>
  <div
    class="menu-dropdown"
    :class="{
      '-active': listActive,
      '-white': theme === 'white',
      '-loud-indigo': theme === 'loud-indigo',
      '-grey': theme === 'grey',
    }"
  >
    <span class="icon" @click="listActive = !listActive">
      <SvgAccordionArrow />
    </span>
    <button @click="listActive = !listActive" class="active-title">
      {{ activeItem?.title }}
    </button>
    <transition name="fade">
      <ul class="menu-dropdown__list" v-if="listActive && items.length">
        <li v-for="item in items" :key="item.id" class="">
          <WSALink
            v-if="!alternateActivePath"
            :to="item.url"
            class="list-link"
            :exactActiveClass="'-link-active'"
            @click="closeMenu(item.url)"
          >
            {{ item.title }}
          </WSALink>
          <WSALink
            v-if="alternateActivePath"
            :to="item.url"
            class="list-link"
            :class="{ '-link-active': item.url === alternateActivePath }"
            @click="closeMenu(item.url)"
          >
            {{ item.title }}
          </WSALink>
        </li>
        <li>
          <div class="newsletter-wrapper" v-if="constants.ENABLE_NEWSLETTER">
            <ButtonCTA
              :to="'#'"
              label="Stay updated"
              class="cta"
              type="fourth"
              :hasIcon="true"
              icon="arrowsimple"
              @click="openOverlay"
            />
          </div>
        </li>
      </ul>
    </transition>
  </div>
</template>

<style lang="postcss" scoped>
.menu-dropdown {
  --bg: var(--deep-indigo);
  --text: var(--white);
  --text-hover: var(--white);
  --text-active: var(--white);

  position: relative;
  isolation: isolate;

  /* padding-block: units(1.5); */
  background-color: var(--bg);
  color: var(--text);

  &.-white {
    --bg: var(--white);
    --text: var(--grey-4);
    --text-active: var(--loud-indigo);
    --text-hover: var(--loud-indigo);
  }

  &.-loud-indigo {
    --bg: var(--loud-indigo);
    --text: var(--white);
    --text-active: var(--white);
    --text-hover: var(--white);
  }

  &.-grey {
    --bg: var(--grey-0);
    --text: var(--grey-4);
    --text-active: var(--loud-indigo);
    --text-hover: var(--loud-indigo);
  }

  &__list {
    position: absolute;
    top: 100%;
    left: 0;
    width: calc(100% + (units(1.5) * 2));
    z-index: 2;

    padding: units(1.5);
    padding-top: units(1);
    /* padding: 0 units(1.5); */
    margin-inline: calc(units(1.5) * -1);

    /* border-top: 1px solid var(--text); */
    background-color: var(--bg);
  }

  .icon {
    position: absolute;
    right: 0;
    top: 50%;
    translate: 0 -50%;
    transform-origin: center center;
    display: block;
    width: 20px;
    height: 20px;
    z-index: 3;

    line-height: 0;
    fill: var(--text);
    transition: rotate 0.3s ease-in-out;
  }

  &.-active {
    .icon {
      rotate: 180deg;
    }
  }

  .list-link,
  .active-title {
    padding-block: units(0.8);
  }

  li:first-child {
    border-top: 1px solid var(--text);
    padding-top: units(1);
  }

  .list-link {
    display: block;
    opacity: 0.6;
    color: var(--text);
    text-decoration: none;
    transition: opacity 0.25s ease;

    &.-link-active {
      opacity: 1;
    }
  }

  .active-title {
    position: relative;
    display: block;
    width: 100%;
    text-align: left;
    opacity: 1;
    color: var(--text);
    appearance: none;
    background-color: transparent;
    border: 0;
    text-decoration: none;
    font-size: units(1.6);

    &:after {
      content: "";
      position: absolute;
      bottom: 0;
      left: 0;
      width: 100%;
      height: 1px;

      opacity: 0;
      background-color: var(--text);
      transition: opacity 0.25s ease;
    }
  }

  .newsletter-wrapper {
    padding-top: units(1.5);
    margin-top: units(1);
    border-top: 1px solid var(--text);

    .cta {
      padding: 0;
      color: var(--text);
      opacity: 0.8;

      :deep(.icon) {
        width: 0.8em;
      }

      @media (--md) {
        padding: units(0.8) units(1.2);
        padding-right: 0;
      }
    }
  }
}
</style>
